import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import DashboardSidebar from '../components/DashboardSidebar';
import DashboardHeader from '../components/DashboardHeader';
import { GuidedTour, useTourStatus } from '../components/GuidedTour';
import CommandCenter from '../components/dashboards/CommandCenter';
import CohortMigration from '../components/dashboards/CohortMigration';
import RFMChurnDashboard from '../components/dashboards/RFMChurnDashboard';
import StorePerformanceDashboard from '../components/dashboards/StorePerformanceDashboard';
import CampaignROIDashboard from '../components/dashboards/CampaignROIDashboard';
import PointsEconomicsDashboard from '../components/dashboards/PointsEconomicsDashboard';
import ExecutiveDashboard from '../components/dashboards/ExecutiveDashboard';
import CustomerDashboard from '../components/dashboards/CustomerDashboard';
import StoreDashboard from '../components/dashboards/StoreDashboard';
import ReportsView from '../components/ReportsView';
import DataReports from '../components/DataReports';
import ReportBuilder from '../components/ReportBuilder';
import CampaignBuilder from '../components/CampaignBuilder';
import CampaignAnalyticsDashboard from '../components/CampaignAnalyticsDashboard';
import BrandAIInsights from '../components/BrandAIInsights';
import SMSBuilder from '../components/SMSBuilder';
import UserManagement from '../components/UserManagement';
import BrandManagement from '../components/BrandManagement';
import PlatformSettings from '../components/PlatformSettings';
import DataSyncHistory from '../components/DataSyncHistory';
import HistoricSync from '../components/HistoricSync';
import DailyScheduler from '../components/DailyScheduler';
import APIMonitor from '../components/APIMonitor';
import AIChat from '../components/AIChat';
import CSVUpload from '../components/CSVUpload';
import CSVUploadProgress from '../components/CSVUploadProgress';
import PrecalculationManager from '../components/PrecalculationManager'; // eslint-disable-line no-unused-vars
import SuperAdminLanding from '../components/SuperAdminLanding';
import VoiceCopilot from '../components/VoiceCopilot';
import LoyaltyConfig from '../components/LoyaltyConfig';
import CustomerTools from '../components/CustomerTools';
import NotificationCenter from '../components/NotificationCenter';
import MetricFormulas from '../components/MetricFormulas';
import SyncStatusBanner from '../components/SyncStatusBanner';
// Fundle Copilot - renamed from VoiceCopilot
import { Brain } from 'lucide-react';

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, checkAuth, isSuperAdmin } = useAuth();
  const { loadBrands, loadThreads, selectedBrand, selectBrand, brands } = useChat();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('command-center');
  const [isLoading, setIsLoading] = useState(true);
  const [authCheckAttempts, setAuthCheckAttempts] = useState(0);
  const [runTour, setRunTour] = useState(false);
  const { hasCompleted: hasDashboardTourCompleted, toursDisabled } = useTourStatus('dashboard');
  const maxAuthAttempts = 3;

  // Auto-start tour for new users (only if tours not disabled)
  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasDashboardTourCompleted && !toursDisabled) {
      // Delay tour start to ensure components are rendered
      const timer = setTimeout(() => setRunTour(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isAuthenticated, hasDashboardTourCompleted, toursDisabled]);

  // Check auth on mount
  useEffect(() => {
    const init = async () => {
      // If we have user from navigation state, we're good
      if (location.state?.user) {
        setIsLoading(false);
        return;
      }
      
      // If already authenticated (e.g. from login -> navigate), no need to re-check
      if (isAuthenticated === true) {
        setIsLoading(false);
        return;
      }
      
      // If isAuthenticated is null (page refresh), verify session via cookie
      if (isAuthenticated === null) {
        const authUser = await checkAuth();
        if (authUser) {
          setIsLoading(false);
        } else if (authCheckAttempts < maxAuthAttempts) {
          // Retry once before giving up - backend may be temporarily busy
          setAuthCheckAttempts(prev => prev + 1);
          await new Promise(r => setTimeout(r, 1500));
          const retryUser = await checkAuth();
          if (retryUser) {
            setIsLoading(false);
          } else {
            navigate('/login');
          }
        } else {
          navigate('/login');
        }
        return;
      }
      
      // Not authenticated
      if (isAuthenticated === false) {
        navigate('/login');
      }
    };
    init();
  }, [checkAuth, navigate, location.state, isAuthenticated]);

  // Load brands when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadBrands();
    }
  }, [isAuthenticated, loadBrands]);

  // Load threads when brand is selected
  useEffect(() => {
    if (selectedBrand) {
      loadThreads(selectedBrand.brand_id);
    }
  }, [selectedBrand, loadThreads]);

  // Loading state
  if (isLoading || isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Brain className="w-12 h-12 text-primary animate-pulse" />
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">
            {authCheckAttempts > 0 
              ? `Verifying session... (${authCheckAttempts}/${maxAuthAttempts})`
              : 'Loading Fundle Brain...'
            }
          </p>
        </div>
      </div>
    );
  }

  const renderActiveView = () => {
    // For Super Admin with no brand selected - show landing page (except for admin views)
    const adminViews = ['brand-management', 'user-management', 'platform-settings', 'data-sync', 'csv-upload', 'notification-center', 'metric-formulas', 'historic-sync', 'daily-scheduler', 'api-monitor'];
    if (isSuperAdmin && !selectedBrand && !adminViews.includes(activeView)) {
      return <SuperAdminLanding brands={brands} onSelectBrand={(brand) => {
        selectBrand(brand);
        setActiveView('command-center');
      }} />;
    }
    
    switch (activeView) {
      case 'command-center':
        return <CommandCenter />;
      case 'cohort-migration':
        return <CohortMigration />;
      case 'rfm-churn':
        return <RFMChurnDashboard />;
      case 'store-performance':
        return <StorePerformanceDashboard />;
      case 'campaign-roi':
        return <CampaignROIDashboard />;
      case 'points-economics':
        return <PointsEconomicsDashboard />;
      case 'executive':
        return <ExecutiveDashboard />;
      case 'customers':
        return <CustomerDashboard />;
      case 'stores':
        return <StoreDashboard />;
      case 'reports':
        return <ReportsView />;
      case 'data-reports':
        return <DataReports />;
      case 'report-builder':
        return <ReportBuilder />;
      case 'campaign-builder':
        return <CampaignBuilder />;
      case 'campaign-analytics':
        return <CampaignAnalyticsDashboard brandId={selectedBrand?.brand_id} />;
      case 'brand-insights':
        return <BrandAIInsights brandId={selectedBrand?.brand_id} brandName={selectedBrand?.name} onNavigateToChat={(msg) => { setActiveView('ai-chat'); }} />;
      case 'sms-builder':
        return <SMSBuilder />;
      case 'user-management':
        return <UserManagement />;
      case 'brand-management':
        return <BrandManagement />;
      case 'platform-settings':
        return <PlatformSettings />;
      case 'data-sync':
        return <DataSyncHistory />;
      case 'historic-sync':
        return <HistoricSync />;
      case 'daily-scheduler':
        return <DailyScheduler />;
      case 'api-monitor':
        return <APIMonitor />;
      case 'ai-chat':
        return <AIChat />;
      case 'csv-upload':
        return <CSVUploadProgress />;
      case 'loyalty-config':
        return <LoyaltyConfig />;
      case 'customer-tools':
        return <CustomerTools />;
      case 'notification-center':
        return <NotificationCenter />;
      case 'metric-formulas':
        return <MetricFormulas />;
      default:
        return <CommandCenter />;
    }
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground" data-testid="dashboard-page">
      {/* Guided Tour - Disabled for now */}
      {/* <GuidedTour 
        tourKey="dashboard" 
        run={runTour} 
        onComplete={() => setRunTour(false)} 
      /> */}
      
      {/* Sidebar */}
      <DashboardSidebar 
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        activeView={activeView}
        onViewChange={setActiveView}
      />
      
      {/* Main area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <DashboardHeader 
          activeView={activeView}
          onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className="flex-1 overflow-auto p-6 bg-muted/20">
          <SyncStatusBanner />
          {renderActiveView()}
        </main>
      </div>
      
      {/* Voice AI Copilot - available on all dashboard views */}
      <VoiceCopilot 
        dashboardType={activeView}
        brandId={selectedBrand?.brand_id || null}
        brandName={selectedBrand?.name || ''}
        onNavigate={setActiveView}
      />
    </div>
  );
}
